/**
 * Shared helpers and utilities for Footwear Studio
 * Used across all Footwear Studio implementations to ensure consistency
 */

import { FIVE_VIEW_SET } from "@shared/views/angleViewSet";
import { isVersionHistorySideJob as isVersionHistorySideJobShared } from "@shared/curationLabels";
import { apiFetch } from "@/lib/apiFetch";

// Type definitions
export type JobListItem = {
  id: string;
  pipeline: string | null;
  status: string;
  created_at: string;
  upload_path?: string | null;
  prompt?: string | null;
  payload?: any;
  detail?: any;
  style_id?: string | null;
  collection_id?: string | null;
  /** Raw job_outputs.url for kind image:2d-side (from /api/style-side-jobs). */
  side_output_url?: string | null;
  /**
   * Version-chip thumb from /api/style-side-jobs: image:2d-side-hub when present,
   * else image:2d-side (same preference as api/styles.ts).
   */
  side_hub_output_url?: string | null;
  /** True when an image:2d-side row exists for this job; null for non-2d_side. */
  has_side_output?: boolean | null;
  /** True when an image:2d-side-hub row exists for this job; null for non-2d_side. */
  has_side_hub_output?: boolean | null;
  side_ref_kind?: string | null;
  /**
   * From /api/style-side-jobs (style_id scope): true when a done 2d_angles
   * job has payload.parent_job_id === this job id.
   */
  has_done_angles?: boolean;
  /** CURATION-C — payload.job_origin (additive mirror). */
  job_origin?: string | null;
  /** CURATION-C — payload.parent_job_id when a string. */
  parent_job_id?: string | null;
  /** CURATION-C — style_version_marks.is_favourite. */
  is_favourite?: boolean;
  /** CURATION-C — style_version_marks.hidden_at != null. */
  is_hidden?: boolean;
  /** CURATION-C — job.id === styles.master_job_id (style scope). */
  is_master?: boolean;
};

/** Phase 0: include side jobs in version history (exclude colorways). */
export function isVersionHistorySideJob(job: JobListItem): boolean {
  return isVersionHistorySideJobShared(job);
}

export type FootwearPipeline = '2d_side' | '2d_angles' | '3d';

// Canonical mapping: job_outputs.kind → UI label (five-view set; V5-a source of truth)
export const ANGLE_KIND_ORDER = FIVE_VIEW_SET.map((v) => ({
  kind: v.kind,
  label: v.label,
}));

export const ANGLE_LIST = ANGLE_KIND_ORDER.map((a) => a.kind);

/** Generated-only views (NB2 loop); Outside/lateral is master alias. */
export const GENERATED_VIEWS = FIVE_VIEW_SET.filter((v) => v.generated);

/** Client kill switch: default ON when unset. */
export function isAnglePickerEnabledClient(): boolean {
  return import.meta.env.VITE_STRONOM_ANGLE_PICKER !== "false";
}

/** galleryKey used by StageAngles preview/chips (stable UI keys, not DB kinds). */
const GALLERY_KEY_BY_KIND: Record<string, string> = {
  "image:angle-lateral": "lateral",
  "image:angle-medial": "medial",
  "image:angle-top": "top",
  "image:angle-bottom": "outsole",
  "image:angle-3q-front": "3_4_r",
};

export const ANGLE_VIEW_CHIPS = ANGLE_KIND_ORDER.map(({ kind, label }) => ({
  galleryKey: GALLERY_KEY_BY_KIND[kind],
  angleKey: kind,
  label,
}));

/**
 * Multi-View track chip display order (presentation only).
 * Rotation around the shoe, then overhead views — never reorder ANGLE_LIST / GENERATED_VIEWS.
 */
export const ANGLE_CHIP_DISPLAY_ORDER = [
  "lateral",
  "3_4_r",
  "medial",
  "top",
  "outsole",
] as const;

export const ANGLE_GALLERY_TILES = ANGLE_VIEW_CHIPS.map(({ galleryKey, label }) => ({
  key: galleryKey,
  label,
}));

// Legacy kind aliases for backwards compatibility
// Maps old simplified kinds to new canonical kinds
export const ANGLE_KIND_ALIASES: Record<string, string> = {
  "lateral": "image:angle-lateral",
  "medial": "image:angle-medial",
  "toe": "image:angle-front",
  "heel": "image:angle-back",
  "top": "image:angle-top",
  "outsole": "image:angle-bottom",
  "3q-left": "image:angle-3q-front",
  "3q-right": "image:angle-3q-back",
};

/** Count of required kinds present in a display map (canonical or legacy keys). */
export function countReadyAngles(displayImages: Record<string, string | undefined>): number {
  return ANGLE_KIND_ORDER.filter(({ kind }) => {
    const legacyEntry = Object.entries(ANGLE_KIND_ALIASES).find(([, canonical]) => canonical === kind);
    const legacyKey = legacyEntry?.[0];
    return !!(displayImages[kind] || (legacyKey && displayImages[legacyKey]));
  }).length;
}

export function hasAllRequiredAngles(displayImages: Record<string, string | undefined>): boolean {
  return countReadyAngles(displayImages) >= ANGLE_KIND_ORDER.length;
}

// Normalize any angle kind to the canonical form
export function normalizeAngleKind(kind: string): string {
  return ANGLE_KIND_ALIASES[kind] || kind;
}

/**
 * Normalize upload context from various sources
 * Handles upload_path, determinism_key, r2d_id, etc.
 */
export function normalizeUploadContext(
  uploadPath?: string | null,
  determinismKey?: string | null,
  r2dId?: string | null
): string | null {
  if (uploadPath) return uploadPath;
  if (determinismKey) return determinismKey;
  if (r2dId) return r2dId;
  return null;
}

/**
 * Extract upload path from a job (checks multiple locations)
 */
export function extractJobUploadPath(job: JobListItem): string | null {
  return (
    job.upload_path ||
    (job.payload as any)?.meta?.upload_path ||
    (job.payload as any)?.upload_url ||
    (job.detail as any)?.meta?.upload_path ||
    (job.detail as any)?.upload_url ||
    null
  );
}

/**
 * Find the last completed job by pipeline, optionally matching upload context
 */
export function findLastJobByPipeline(
  jobs: JobListItem[],
  pipeline: FootwearPipeline,
  uploadContext?: string | null
): JobListItem | undefined {
  let filtered = [...jobs]
    .filter(j => j.pipeline === pipeline && j.status === 'done');
  
  // If uploadContext is provided, filter to match
  if (uploadContext) {
    filtered = filtered.filter(j => {
      const jobUploadPath = extractJobUploadPath(j);
      // Also check r2d_id if available (FootwearStudioV2 uses r2d_id)
      const jobR2dId = (j.payload as any)?.r2d_id;
      return jobUploadPath === uploadContext || jobR2dId === uploadContext;
    });
  }
  
  return filtered
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
}

export type StudioJobPipeline = "2d_side" | "2d_angles" | "3d";

export type FetchStudioJobsOpts = {
  styleId?: string | null;
  uploadPath?: string | null;
  orgKey?: string;
  limit?: number;
  /** Default: 2d_side only (legacy Master / version list). */
  pipelines?: StudioJobPipeline[];
};

/** Raw `/api/style-side-jobs` JSON (CURATION-C adds master_job_id). */
export type StyleSideJobsApiJson = {
  ok: boolean;
  style_id?: string | null;
  org_key?: string;
  /** CURATION-C — styles.master_job_id when style_id-scoped; null for upload_path. */
  master_job_id?: string | null;
  master_output_kind?: string;
  master_hub_output_kind?: string;
  jobs?: JobListItem[];
  error?: string;
};

/**
 * Style- or upload_path-scoped done jobs via service-role `/api/style-side-jobs`.
 * Client supabase SELECT on `jobs` is blocked (`jobs_select_none`).
 * Returns the jobs array only; see StyleSideJobsApiJson.master_job_id for the
 * top-level master field (available on the wire; not returned here).
 */
export async function fetchStudioJobs(
  opts: FetchStudioJobsOpts,
): Promise<JobListItem[]> {
  const json = await fetchStudioJobsJson(opts);
  return json?.jobs ?? [];
}

/** Wire JSON for `/api/style-side-jobs` (keeps top-level master_job_id). */
export async function fetchStudioJobsJson(
  opts: FetchStudioJobsOpts,
): Promise<StyleSideJobsApiJson | null> {
  const styleId =
    typeof opts.styleId === "string" && opts.styleId.trim()
      ? opts.styleId.trim()
      : "";
  const uploadPath =
    typeof opts.uploadPath === "string" && opts.uploadPath.trim()
      ? opts.uploadPath.trim()
      : "";
  if (!styleId && !uploadPath) return null;
  if (styleId && uploadPath) return null;

  try {
    const params = new URLSearchParams({
      org_key: opts.orgKey ?? "brand.com",
      limit: String(opts.limit ?? 50),
    });
    if (styleId) params.set("style_id", styleId);
    if (uploadPath) params.set("upload_path", uploadPath);
    if (opts.pipelines && opts.pipelines.length > 0) {
      params.set("pipelines", opts.pipelines.join(","));
    }
    const res = await apiFetch(`/api/style-side-jobs?${params.toString()}`);
    const data = (await res.json()) as StyleSideJobsApiJson;
    if (!data?.ok || !Array.isArray(data.jobs)) return null;
    return data;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.error("[FootwearStudio] Error fetching studio jobs:", err);
    }
    return null;
  }
}

/**
 * Fetch done 2d_side jobs for one style (default pipelines).
 * Each job may include `side_output_url`: raw job_outputs.url for kind
 * `image:2d-side` (Master's real output kind).
 */
export async function fetchStyleSideJobs(
  styleId: string,
  orgKey: string = "brand.com",
  limit: number = 50
): Promise<JobListItem[]> {
  if (!styleId) return [];
  return fetchStudioJobs({ styleId, orgKey, limit });
}

/**
 * CURATION-F2 — style-scoped side jobs plus top-level styles.master_job_id.
 * Existing fetchStudioJobs / fetchStyleSideJobs callers unchanged.
 */
export async function fetchStyleSideJobsWithMaster(
  styleId: string,
  orgKey: string = "brand.com",
  limit: number = 50,
): Promise<{ jobs: JobListItem[]; masterJobId: string | null }> {
  if (!styleId) return { jobs: [], masterJobId: null };
  const json = await fetchStudioJobsJson({ styleId, orgKey, limit });
  if (!json) return { jobs: [], masterJobId: null };
  const mid = json.master_job_id;
  return {
    jobs: json.jobs ?? [],
    masterJobId:
      typeof mid === "string" && mid.trim() ? mid.trim() : null,
  };
}

/**
 * Validate parent job for 2D Angles/3D generation
 * Returns error message if validation fails, null if valid
 */
export function validateParentJob(
  parentJob: JobListItem | undefined,
  taskKind: '2d-angles' | '3d'
): { valid: false; error: string } | { valid: true } {
  if (!parentJob) {
    return {
      valid: false,
      error: taskKind === '2d-angles'
        ? "You must generate and complete a 2D side view before queuing 2D Angles."
        : "You must generate and complete 2D angles before queuing 3D."
    };
  }

  // 2D Angles requires a 2D Side parent
  if (taskKind === '2d-angles') {
    if (parentJob.pipeline !== '2d_side') {
      return {
        valid: false,
        error: "Parent job must be a 2D Side job. Please generate a 2D side view first."
      };
    }
    if (parentJob.status !== 'done') {
      return {
        valid: false,
        error: `The 2D Side job is still ${parentJob.status}. Please wait for it to complete before queuing 2D Angles.`
      };
    }
  }

  // 3D requires a 2D Angles parent
  if (taskKind === '3d') {
    if (parentJob.pipeline !== '2d_angles') {
      return {
        valid: false,
        error: "Parent job must be a 2D Angles job. Please generate 2D angles first."
      };
    }
    if (parentJob.status !== 'done') {
      return {
        valid: false,
        error: `The 2D Angles job is still ${parentJob.status}. Please wait for it to complete before queuing 3D.`
      };
    }
  }

  return { valid: true };
}

/**
 * Central error logging helper for Footwear Studio
 * Logs detailed errors in development, returns user-friendly messages
 */
export function logStudioError(context: string, error: unknown): string {
  if (process.env.NODE_ENV !== "production") {
    // Log detailed info in dev
    console.error(`[FootwearStudio] ${context}`, error);
  }

  // Try to derive a human-readable message
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    // If it's a fetch/response error with JSON { error }
    const anyErr = error as any;
    if (anyErr.error && typeof anyErr.error === "string") {
      return anyErr.error;
    }
    if (anyErr.message && typeof anyErr.message === "string") {
      return anyErr.message;
    }
  }

  // Fallback generic message
  return "Something went wrong. Please try again.";
}

/**
 * Safely parse JSON from a Response, falling back to text if parsing fails
 */
export async function safeJsonFromResponse(res: Response): Promise<{
  json: any;
  text: string;
  parseError: Error | null;
}> {
  const text = await res.text();

  // Try JSON first
  try {
    const json = text ? JSON.parse(text) : null;
    return { json, text, parseError: null };
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[FootwearStudio] Failed to parse JSON from response', {
        status: res.status,
        statusText: res.statusText,
        bodySnippet: text?.slice(0, 200),
        err,
      });
    }
    return { json: null, text, parseError: err as Error };
  }
}

/** Shared terminal check for all studio job polls (Phase 0). */
export function isTerminalStatus(status: string | null | undefined): boolean {
  return status === "done" || status === "error" || status === "cancelled";
}

/** Studio poll cadence: 150 attempts × 2s ≈ 5 min cap. */
export const STUDIO_POLL_ATTEMPT_CAP = 150;
export const STUDIO_POLL_INTERVAL_MS = 2000;

/** Review Console generation outcome (Phase 0 plumbing). */
export type ReviewConsoleOutcome =
  | "none"
  | "processing"
  | "done"
  | "failed"
  | "cancelled";

/** Derive master-side Review Console outcome from job status + preview presence. */
export function resolveReviewConsoleOutcome(
  jobStatus: { status: string; stage: string },
  sidePreviewImage: string | null,
): ReviewConsoleOutcome {
  if (jobStatus.status === "cancelled") return "cancelled";
  if (jobStatus.status === "error") return "failed";
  if (
    jobStatus.stage === "side_preview" &&
    (jobStatus.status === "processing" || jobStatus.status === "pending")
  ) {
    return "processing";
  }
  if (sidePreviewImage) return "done";
  return "none";
}

export type JobPollFetchResult = {
  ok: boolean;
  job?: {
    status?: string;
    pipeline?: string;
    id?: string;
    payload?: Record<string, unknown>;
    [key: string]: unknown;
  };
  line_check?: Record<string, unknown> | null;
  error?: string;
};

export type StudioJobPollFailedReason =
  | "fetch_error"
  | "invalid_response"
  | "attempt_cap";

/** Extra /api/job polls after done while waiting for job_line_checks (~100 s). */
export const LINE_CHECK_EXTRA_POLL_CAP = 50;

export interface StudioJobPollHandlers {
  onDone: (
    job: NonNullable<JobPollFetchResult["job"]>,
    jobData: JobPollFetchResult,
  ) => void | Promise<void>;
  onError?: (
    job: NonNullable<JobPollFetchResult["job"]>,
    jobData: JobPollFetchResult,
  ) => void;
  onCancelled?: (
    job: NonNullable<JobPollFetchResult["job"]>,
    jobData: JobPollFetchResult,
  ) => void;
  onProcessing?: (
    job: NonNullable<JobPollFetchResult["job"]>,
    jobData: JobPollFetchResult,
  ) => void;
  /**
   * VERDICT-UI: after onDone for contract 2d_side jobs — updates line-check state.
   * waiting=true while extra polls may still run; waiting=false when row arrived or cap hit.
   */
  onLineCheckUpdate?: (
    update: {
      lineCheck: Record<string, unknown> | null;
      waiting: boolean;
    },
    jobData: JobPollFetchResult,
  ) => void;
  onPollFailed: (
    reason: StudioJobPollFailedReason,
    detail?: unknown,
  ) => void;
}

/** Returns true while the poll should reschedule (non-terminal job status). */
export function shouldContinuePolling(
  status: string | null | undefined,
): boolean {
  return !isTerminalStatus(status);
}

/** True when a done 2d_side job with contract_summary.used still needs line_check. */
export function needsLineCheckExtraPoll(
  job: JobPollFetchResult["job"] | undefined,
  lineCheck: JobPollFetchResult["line_check"],
): boolean {
  if (!job || job.pipeline !== "2d_side") return false;
  const cs = job.payload?.contract_summary as { used?: boolean } | undefined;
  if (cs?.used !== true) return false;
  return lineCheck == null;
}

/**
 * Shared studio job poll loop with attempt cap and terminal routing.
 * All six poll sites (5 studio + JobsTray) route terminal checks through isTerminalStatus.
 *
 * VERDICT-UI: after first onDone for contract 2d_side jobs, keeps polling until
 * line_check arrives or LINE_CHECK_EXTRA_POLL_CAP extra polls elapse. onDone runs once.
 */
export function startStudioJobPoll(
  jobId: string,
  handlers: StudioJobPollHandlers,
  opts?: {
    expectedPipeline?: string;
    intervalMs?: number;
    attemptCap?: number;
    lineCheckExtraPollCap?: number;
  },
): void {
  let attempts = 0;
  let doneFired = false;
  let lineCheckExtraPolls = 0;
  const intervalMs = opts?.intervalMs ?? STUDIO_POLL_INTERVAL_MS;
  const attemptCap = opts?.attemptCap ?? STUDIO_POLL_ATTEMPT_CAP;
  const expectedPipeline = opts?.expectedPipeline;
  const lineCheckExtraCap =
    opts?.lineCheckExtraPollCap ?? LINE_CHECK_EXTRA_POLL_CAP;

  const poll = async () => {
    attempts++;
    if (attempts > attemptCap) {
      handlers.onPollFailed("attempt_cap");
      return;
    }
    try {
      const res = await apiFetch(`/api/job?id=${encodeURIComponent(jobId)}`);
      const jobData = (await res.json()) as JobPollFetchResult;

      if (!jobData?.ok || !jobData.job) {
        handlers.onPollFailed("invalid_response", jobData);
        return;
      }

      const job = jobData.job;
      const status = job.status ?? "";

      if (
        status === "done" &&
        (!expectedPipeline || job.pipeline === expectedPipeline)
      ) {
        if (!doneFired) {
          doneFired = true;
          await handlers.onDone(job, jobData);
        }

        const lc = jobData.line_check ?? null;
        const cs = job.payload?.contract_summary as { used?: boolean } | undefined;
        if (cs?.used === true) {
          if (lc != null) {
            handlers.onLineCheckUpdate?.(
              { lineCheck: lc, waiting: false },
              jobData,
            );
            return;
          }
          if (lineCheckExtraPolls < lineCheckExtraCap) {
            lineCheckExtraPolls += 1;
            handlers.onLineCheckUpdate?.(
              { lineCheck: null, waiting: true },
              jobData,
            );
            setTimeout(poll, intervalMs);
            return;
          }
          handlers.onLineCheckUpdate?.(
            { lineCheck: null, waiting: false },
            jobData,
          );
        }
        return;
      }

      if (status === "error") {
        handlers.onError?.(job, jobData);
        return;
      }

      if (status === "cancelled") {
        handlers.onCancelled?.(job, jobData);
        return;
      }

      if (shouldContinuePolling(status)) {
        handlers.onProcessing?.(job, jobData);
        setTimeout(poll, intervalMs);
        return;
      }

      handlers.onPollFailed("invalid_response", { status });
    } catch (err) {
      handlers.onPollFailed("fetch_error", err);
    }
  };

  setTimeout(poll, intervalMs);
}
